import type { CancelablePromise, CommonOption } from "@shared/ui";
import { PromisePool } from "@shared/ui";
import autobind from "autobind-decorator";
import classNames from "classnames";
import type React from "react";
import type { ReactNode } from "react";
import { Form } from "react-bootstrap";
import type { FieldRenderProps } from "react-final-form";
import type { FormItemProps } from "./FormItem";
import { FormItem } from "./FormItem";

export interface FormRadioOption<T, L extends ReactNode = ReactNode> extends CommonOption<T, L> {
	id?: string;
}

export type FormRadioOptions<T, L extends ReactNode = ReactNode> = FormRadioOption<T, L>[];

export interface FormRadioOptionsFn<T = string, L extends ReactNode = ReactNode> {
	id: string;
	fn: () => Promise<FormRadioOptions<T, L>> | CancelablePromise<FormRadioOptions<T, L>>;
}

export interface FormRadioProps<T, L extends ReactNode = ReactNode> extends FormItemProps<T> {
	options: FormRadioOptions<T, L> | FormRadioOptionsFn<T, L>;
	variant?: "button" | "centered";
	inlineOptions?: true | "sm" | "md" | "lg" | "xl";
	inputClasses?: string;
	boldFirstOption?: boolean;
}

export interface FormRadioState<T = string> {
	options: FormRadioOptions<T>;
	error: boolean;
}

export class FormRadio<T = string> extends FormItem<T, FormRadioProps<T>, FormRadioState<T>> {
	private readonly promisePool = new PromisePool();
	protected lastOptionsId: string = undefined;
	protected readonly defaultValue = undefined;

	public constructor(props: FormRadioProps<T>) {
		super(props);

		this.state = {
			options: Array.isArray(props.options) ? props.options : [],
			error: false,
		};
	}

	public componentDidMount(): void {
		super.componentDidMount();

		this.updateOptions();
	}

	public componentDidUpdate(prevProps: FormRadioProps<T>, prevState: FormRadioState<T>): void {
		super.componentDidUpdate(prevProps, prevState);

		this.updateOptions();
	}

	public componentWillUnmount(): void {
		super.componentWillUnmount();

		this.promisePool.cancel();
	}

	@autobind
	private updateOptions(force?: boolean): void {
		const { options } = this.props;
		const { options: stateOptions } = this.state;

		const checkNewValue = () => {
			if (!this.state.options.find((item) => item.value === this.lastValue)) {
				this.handleProtectedChange(undefined);
			}
		};

		if (!Array.isArray(options)) {
			if (force || options.id !== this.lastOptionsId) {
				this.lastOptionsId = options.id;
				this.promisePool
					.add(options.fn())
					.then((realOptions) => this.setState({ options: realOptions, error: false }, checkNewValue))
					.catch(() => {
						this.setState({
							error: true,
						});

						this.handleProtectedChange(undefined);
					});
			}
		} else if (JSON.stringify(options) !== JSON.stringify(stateOptions)) {
			this.lastOptionsId = undefined;
			this.setState(
				{
					options,
					error: false,
				},
				checkNewValue,
			);
		}
	}

	protected renderField(fieldProps: FieldRenderProps<T>): ReactNode {
		const { variant, inlineOptions, readOnly, disabled, tabIndex, inputClasses = "", boldFirstOption } = this.props;
		const { options } = this.state;
		const {
			input: { name, value },
		} = fieldProps;

		let className = `input-group ${inputClasses}`;
		const boldLabelClassName = boldFirstOption ? "font-weight-bold" : "";
		if (!inlineOptions) {
			className += " d-flex flex-column";
		} else if (typeof inlineOptions === "string") {
			className += ` d-${inlineOptions}-block d-flex flex-column`;
		}

		return (
			<div className={className}>
				{options?.map((option, key) => (
					<Form.Check
						key={key}
						id={option.id || `${this.id}-${key}`}
						className={classNames(
							key === 0 && boldLabelClassName,
							variant === "button" && "custom-radio-btn",
							variant === "centered" && "mx-auto",
						)}
						inline={!!inlineOptions}
						readOnly={readOnly}
						disabled={disabled || readOnly}
						tabIndex={tabIndex}
						onChange={() => {
							this.handleChange(option.value, fieldProps);
						}}
						onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
							this.handleFocus(event, fieldProps);
						}}
						onBlur={(event: React.FocusEvent<HTMLElement>) => {
							this.handleBlur(event, fieldProps);
						}}
						custom
						type="radio"
						name={name}
						value={String(option.value)}
						label={option.label}
						checked={value === option.value}
					/>
				))}
			</div>
		);
	}

	@autobind
	protected handleValidate(value: T, allValues: Record<string, any>): string {
		const { validate, required } = this.props;

		if (this.isDisabled()) {
			return undefined;
		}

		if (validate) {
			return validate(value, allValues);
		}

		if (
			required &&
			((typeof value !== "boolean" && !value) || (typeof value === "boolean" && value === undefined))
		) {
			return "Kérjük válassz ki egy elemet!";
		}
	}

	public static stringArrayToOptions(source: Array<string>): FormRadioOptions<string> {
		return source.map(
			(option: string) =>
				({
					label: option,
					value: option,
				}) as CommonOption<string>,
		);
	}
}
